import {useCallback, useEffect, useRef, useState} from 'react';

import {useAppSelector} from '@/app/store';
import {selectToken} from '@/app/store/tokenSlice';
import {TKYCInReviewSubStates, TStatusKYC2} from '@/constants/KycConstants';
import logError from '@/utils/errors/logError';

interface WebSocketConfig {
  protocols?: string | string[];
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  reconnectAttempts?: number;
  reconnectInterval?: number; // in milliseconds
}

export interface IKycWebSocketData {
  kycCurrentState: TStatusKYC2;
  subStateInReview?: TKYCInReviewSubStates;
}

// Derived from WebSocket.send's own signature instead of a hand-written union
// so it always matches exactly what `send()` accepts on whichever lib.dom.d.ts
// TypeScript resolves (its ArrayBufferView/BufferSource typings vary by version).
type WebSocketSendData = Parameters<WebSocket['send']>[0];

export const useWebSocket = ({
  protocols,
  onOpen,
  onClose,
  onError,
  onMessage,
  reconnectAttempts = 5,
  reconnectInterval = 3000,
}: WebSocketConfig) => {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [data, setData] = useState<IKycWebSocketData | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const token = useAppSelector(selectToken);
  const webSocketUrl = import.meta.env.VITE_APIS_WEBSOCKET_URL;

  const sendMessage = useCallback((message: WebSocketSendData) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
    } else {
      logError(new Error('WebSocket Send Message Error'), {
        fileName: 'useWebSocketService.ts',
        functionName: 'sendMessage',
      });
    }
  }, []);

  const connect = useCallback(() => {
    if (socketRef.current) {
      console.warn('WebSocket already connected');
      return;
    }

    const socket = new WebSocket(
      `${webSocketUrl}/kyc-ws?authorization=${token}`,
      protocols,
    );

    socket.onopen = event => {
      setIsConnected(true);
      setReconnectCount(0);
      onOpen?.(event);
    };

    socket.onclose = event => {
      setIsConnected(false);
      onClose?.(event);

      if (reconnectCount < reconnectAttempts) {
        setTimeout(() => {
          setReconnectCount(prev => prev + 1);
          connect();
        }, reconnectInterval);
      }
    };

    socket.onerror = event => {
      onError?.(event);
    };

    socket.onmessage = event => {
      try {
        const parsedData = JSON.parse(event.data);
        setData(parsedData);

        if (!parsedData) {
          console.warn('No data received from WebSocket');
          return;
        }

        if (!parsedData.kycCurrentState) {
          console.warn('No state in the WebSocket data');
          return;
        }
      } catch (error) {
        logError(error, {
          fileName: 'useWebSocketService',
          functionName: 'connect',
        });
      }

      onMessage?.(event);
    };

    socketRef.current = socket;
  }, [
    webSocketUrl,
    protocols,
    onOpen,
    onClose,
    onError,
    onMessage,
    reconnectAttempts,
    reconnectInterval,
    reconnectCount,
    token,
  ]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      if (socketRef.current.readyState !== WebSocket.CONNECTING)
        socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      // disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    data, // Expose the WebSocket data
    sendMessage,
    disconnect,
  };
};
